import {
  GroupSecurity,
  RelatedPersonRelationship,
  createReference,
} from '@quromedical/fhir-common';
import { PersonGeneral, PersonContact } from '@quromedical/models';
import { first } from '@quromedical/utils';
import { Alert, Section, Skeleton, SkeletonProvider, Snackbar, Visible } from 'design-system';
import { FormikErrors } from 'formik';
import { onMapsPress } from 'helpers/platform-links';
import { useUserSession } from 'hooks';
import { getPlaceAddressPartialFetcher, googleSearchAddressFetcher } from 'integration';
import { NextOfKinApi } from 'integration/aggregate';
import React, { useCallback, useState } from 'react';
import { JointFields, initialNextOfKin, NextOfKinForm, nextOfKinFormSchema } from 'screens/patient';
import { PersonContactForm, PersonGeneralForm } from 'screens/person';
import { strings } from 'strings';
import { trpc } from 'trpc';

interface PatientNextOfKinTabProps {
  patientId: string;
}

const api = new NextOfKinApi();

const Placeholder: React.FC = () => (
  <Section>
    <Skeleton>
      <NextOfKinForm />
    </Skeleton>
  </Section>
);

export const PatientNextOfKinTab: React.FC<PatientNextOfKinTabProps> = ({ patientId }) => {
  const session = useUserSession();

  const [showAlert, setAlert] = useState<boolean>(true);
  const [submitted, setSubmitted] = useState(false);
  const [nextOfKinError, setNextOfKinError] = useState(false);
  const [nextOfKin, setNexOfKin] = useState<Partial<JointFields>>();

  const closeAlert = useCallback(() => {
    setAlert(false);
  }, []);

  const { data, isFetching: isValidating, error, isFetched, refetch } =
    //TODO this should be a webhook
    trpc.context.relatedPerson.getAll.useQuery({
      patient: patientId,
      relationship: RelatedPersonRelationship.NextOfKin,
    });

  const canEdit = session.hasAny([GroupSecurity.PatientUpdate, GroupSecurity.RelatedPersonUpdate]);

  const existingNextOfKin = first(data?.relatedPersons);
  const relatedPersonId = existingNextOfKin?.id;
  const general = existingNextOfKin?.general;
  const contact = existingNextOfKin?.contact;

  const onGeneralSubmit = useCallback(
    async (formData: PersonGeneral) => {
      await api.updateGeneralNextOfKin({
        id: relatedPersonId as string,
        general: formData,
      });
    },
    [relatedPersonId]
  );

  const onContactSubmit = useCallback(
    async (formData: PersonContact) => {
      await api.updateContactNextOfKin(relatedPersonId as string, formData);
    },
    [relatedPersonId]
  );
  const isButtonDisabled = !existingNextOfKin?.contact?.addressCoordinates;

  const onMapsPressed = useCallback(() => {
    onMapsPress(existingNextOfKin?.contact);
  }, [existingNextOfKin?.contact]);

  const onNextOfKinChange = useCallback(
    (data: Partial<JointFields>, err: FormikErrors<Partial<PersonContact>>) => {
      const hasErrors = !!Object.keys(err).length || !nextOfKinFormSchema.safeParse(data).success;

      setNexOfKin(data);
      setNextOfKinError(hasErrors);
    },
    []
  );

  const onNextOfKinSubmit = useCallback(async () => {
    setSubmitted(true);
    if (!nextOfKinError) {
      await api.createNextOfKin({
        contact: { ...nextOfKin },
        general: { ...nextOfKin },
        patient: createReference('Patient', patientId),
        relationship: RelatedPersonRelationship.NextOfKin,
      });
    }
  }, [nextOfKin, nextOfKinError, patientId]);

  const showErrors = submitted && nextOfKinError;
  const showForm = !error && !existingNextOfKin && isFetched;

  return (
    <SkeletonProvider loading={isValidating}>
      <Visible if={!isFetched}>
        <Placeholder />
      </Visible>

      <Visible if={showForm}>
        <Section hasBottomMargin>
          <Skeleton>
            <NextOfKinForm
              initial={initialNextOfKin}
              showErrors={showErrors}
              onChange={onNextOfKinChange}
              onSubmit={onNextOfKinSubmit}
              addressFetcher={googleSearchAddressFetcher}
              getAddressPartial={getPlaceAddressPartialFetcher}
              revalidate={refetch}
            />
          </Skeleton>
        </Section>
      </Visible>

      <Visible if={!error && existingNextOfKin}>
        <Section>
          <Skeleton>
            <PersonGeneralForm
              canEdit={canEdit}
              data={general}
              onSubmit={onGeneralSubmit}
              revalidate={refetch}
              minimalData={true}
            />
          </Skeleton>
        </Section>

        <Section hasBottomMargin>
          <Skeleton>
            <PersonContactForm
              canEdit={canEdit}
              data={contact}
              onSubmit={onContactSubmit}
              revalidate={refetch}
              hasEmergencyAccess={false}
              addressFetcher={googleSearchAddressFetcher}
              getAddressPartial={getPlaceAddressPartialFetcher}
              onMapsPress={onMapsPressed}
              isButtonDisabled={isButtonDisabled}
            />
          </Skeleton>
        </Section>
      </Visible>

      <Visible if={error}>
        <Snackbar isOpen={showAlert} onClose={closeAlert}>
          <Alert
            actionIcon="close"
            backgroundColor="status-critical"
            onAction={closeAlert}
            title={strings.AlertError}
            body={strings.NextOfKinError}
          />
        </Snackbar>
      </Visible>
    </SkeletonProvider>
  );
};
